import type { ElementRef } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { UntypedFormGroup } from '@angular/forms';
import { AbstractControl, Validators } from '@angular/forms';

import { Subject, debounceTime, distinctUntilChanged, map, startWith } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';
import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';
import {
  INPUT_DEBOUNCE_TIME,
  MonitoringLogger,
  capitalize as capitalizeString,
  trimAndReplaceWhitespace,
} from '@fcom/core';

import { IconButtonSize, IconButtonTheme } from '../../../components/buttons';
import type { IconSet } from '../../../components/icons';
import type { AriaOptions } from '../../interfaces';
import type { PopoverOptions } from '../../popover';
import { CursorType, InputType, TextInputType } from '../enums';

@Component({
  selector: 'fcom-text-input',
  templateUrl: './text-input.component.html',
  styleUrls: ['text-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TextInputComponent {
  themeService = inject(FinnairThemeService);
  parentForm = input.required<UntypedFormGroup>();

  /**
   * Name of this control.
   * Will also be used in this components `data-testid` and `data-testid`s that are passed to child `<fcom-icon-button>`s
   */
  controlName = input.required<string>();

  helpText = input<string>();
  translationPrefix = input<string>();
  placeHolder = input<string | null>(null);
  placeHolderText = input<string>();
  characterCount = input<number>();
  characterLimit = input<number>();
  isSensitive = input(false);
  isResizeable = input(false);
  type = input(TextInputType.INPUT);
  rows = input(6);
  id = input<string>();
  labelPrefix = input<string>();
  labelSuffix = input<string>();
  label = input<string>();
  displayMargin = input(true);
  /** Accepted for backwards compatibility — currently has no visual effect. */
  visualDisable = input(false);
  required = input<boolean>();
  translationErrorPrefix = input<string>();
  hideErrorLabel = input(false);
  hideLabel = input<boolean>();
  inputType = input(InputType.TEXT);
  tooltipHeading = input<string>();
  tooltipContent = input<string>();
  useCustomTooltipAction = input(false);
  tooltipOptions = input<PopoverOptions>();
  icon = input<IconSet>();
  ariaOptions = input<AriaOptions>({});
  isPassword = input(false);
  capitalize = input(false);
  uppercase = input(false);
  clearInput = input(false);
  disableAutoComplete = input<boolean>();
  cursorType = input<CursorType>();
  trim = input(true);

  tooltipClicked = output<Event>();
  inputEvent = output<Event>();
  blurEvent = output<FocusEvent>();

  readonly control = computed<AbstractControl | null>(() => {
    const control = this.parentForm().get(this.controlName());
    const errorMessage = `Control "${this.controlName()}" was not found in parent form.`;
    if (!control) {
      this.monitoringLogger.error(errorMessage, { controlName: this.controlName() });
      throw new Error(errorMessage);
    }
    return control;
  });
  readonly derivedCharacterCount = signal(0);
  readonly idOrControlName = computed<string>(() => this.id() ?? this.controlName());
  readonly inputElement = viewChild<ElementRef<HTMLInputElement>>('inputElement');
  readonly isPasswordVisible = signal(false);
  readonly isRequired = computed<boolean>(() =>
    Boolean(this.required() || this.control().errors?.['required'] || this.control().hasValidator(Validators.required))
  );
  readonly resolvedCharacterCount = computed<number>(() => {
    const count = this.characterCount();

    if (count !== undefined) {
      return count;
    }

    return this.derivedCharacterCount();
  });
  readonly showAsterisk = computed<boolean>(() => this.required() ?? this.isRequired());
  readonly textareaElement = viewChild<ElementRef<HTMLTextAreaElement>>('textareaElement');

  readonly CursorType = CursorType;
  readonly IconButtonSize = IconButtonSize;
  readonly IconButtonTheme = IconButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly InputType = InputType;
  readonly TextInputType = TextInputType;
  readonly FinnairTheme = FinnairTheme;

  private readonly inputSubject = new Subject<Event>();
  private readonly monitoringLogger = inject(MonitoringLogger);

  constructor() {
    effect((onCleanup): void => {
      const control = this.control();

      const subscription = control.valueChanges.pipe(startWith(control.value)).subscribe((value) => {
        this.derivedCharacterCount.set(typeof value === 'string' ? value.length : 0);
      });

      onCleanup((): void => {
        subscription.unsubscribe();
      });
    });

    this.inputSubject
      .pipe(
        debounceTime(INPUT_DEBOUNCE_TIME),
        map((event) => event.target as HTMLInputElement),
        distinctUntilChanged((prev, curr) => prev.value === curr.value),
        takeUntilDestroyed()
      )
      .subscribe((inputValue) => {
        const isUppercase = this.uppercase();
        const isCapitalize = this.capitalize();

        if (!isUppercase && !isCapitalize) {
          return;
        }

        const transform = isUppercase
          ? (value: string) => value.toUpperCase()
          : (value: string) => capitalizeString(value);

        this.formatValue(inputValue, transform);
      });
  }

  handleIconClick(): void {
    if (this.isPassword()) {
      this.togglePasswordVisibility();
    } else {
      this.clearInputField();
    }
  }

  togglePasswordVisibility(): void {
    this.isPasswordVisible.set(!this.isPasswordVisible());
  }

  clearInputField(): void {
    this.control().setValue('');
  }

  private formatValue(input: HTMLInputElement, transform: (value: string) => string): void {
    const { value, selectionStart } = input;
    const newValue = transform(value);

    if (value !== newValue) {
      this.control().setValue(newValue);
      input.setSelectionRange(selectionStart, selectionStart);
    }
  }

  onInput(event: Event): void {
    if (this.uppercase() || this.capitalize()) {
      this.inputSubject.next(event);
    }
    this.inputEvent.emit(event);
  }

  setTrimmedValueIfItIsDifferent(): void {
    const current = this.control().getRawValue();
    const trimmed = trimAndReplaceWhitespace(current);

    if (trimmed !== current) {
      this.control().setValue(trimmed);
    }
  }

  onBlur(event: FocusEvent): void {
    if (this.trim()) {
      this.setTrimmedValueIfItIsDifferent();
    }

    this.blurEvent.emit(event);
  }

  onEnter(): void {
    if (this.trim()) {
      this.setTrimmedValueIfItIsDifferent();
    }
  }

  onPaste(): void {
    if (!this.trim()) {
      return;
    }

    // Without this the field value does not include the pasted content.
    setTimeout(() => {
      this.setTrimmedValueIfItIsDifferent();
    }, 0);
  }
}
